import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Campaign } from "../../../model/SPAModels/campaigns/campaign.Model"
import { RequestStatus } from "../../../model/ServiceRequestStatus.model";
import { getCampaignsList } from "../../../services/SPAAdmin/CampaignsList.api";
import { SPA_HELP_MESSAGE } from "../SPAConstants";

export interface CampaignListState {
  campaignList: Campaign[],
  status: RequestStatus,
  currPage: number,
  totalPage: number,
  filters: any,
  error: string | undefined
  campaignRules: any
}

const initialState: CampaignListState = {
  campaignList: [],
  status: RequestStatus.idle,
  currPage: 1,
  totalPage: 1,
  filters: {},
  error: '',
  campaignRules: {}
}

function getFiscalQuarterStart(date: Date): Date {
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  if (month >= 1 && month <= 3) return new Date(year, 0, 1);      // Jan 1
  if (month >= 4 && month <= 6) return new Date(year, 3, 1);      // Apr 1
  if (month >= 7 && month <= 9) return new Date(year, 6, 1);      // Jul 1
  return new Date(year, 9, 1);                                    // Oct 1
}

const userFunctions = (campaigns: Campaign[]) => {
  const campaignRules: any = {};
  const today = new Date();
  const fiscalQuarterStart = getFiscalQuarterStart(today);

  for (const campaign of campaigns) {
    if (!campaign.summary) continue;

    const expiryDate= campaign.expiryDate ? new Date(campaign.expiryDate) : undefined;
    if( expiryDate && expiryDate < today) continue;

    const createdOnStr = campaign.summary.find(attr => attr.key === "CreatedOn")?.value;
    if (!createdOnStr) continue;
    const datePart = createdOnStr.split(" ")[0];
    const createdOn = new Date(datePart);    

    if (createdOn < fiscalQuarterStart || createdOn > today || campaign.status!=='Published') continue;

    const campaignType = campaign.summary.find(attr => attr.key === "CampaignType")?.value;
    const solutionPlay = campaign.summary.find(attr => attr.key === "SolutionPlay")?.value;
    
    if (!campaignType || !solutionPlay) continue;

    if (!campaignRules[campaignType]) {
      campaignRules[campaignType] = {};
    }
    campaignRules[campaignType][solutionPlay] = (campaignRules[campaignType][solutionPlay] || 0) + 1;
  }

  return campaignRules;
};

export const CampaignListSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    changePageNumber: (state, action: PayloadAction<number>) => {
      state.currPage = action.payload
    },
    setCampaignFilters: (state, action: PayloadAction<any>) => {
      state.filters = action.payload;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(getCampaignsList.pending, (state, action) => {
        state.status = RequestStatus.loading
      })
      .addCase(getCampaignsList.fulfilled, (state, action: PayloadAction<Campaign[]>) => {
        state.status = RequestStatus.succeeded
        state.campaignList = action.payload;
        state.campaignRules = userFunctions(action.payload);
        state.error = undefined
      })
      .addCase(getCampaignsList.rejected, (state, action: any) => {
        state.status = RequestStatus.failed
        const message = (action?.payload && action.payload["Message"] ? action.payload["Message"] : action.error.message);
        state.error = SPA_HELP_MESSAGE;
      })
  }
});

export const {
  changePageNumber,
  setCampaignFilters
} = CampaignListSlice.actions;

export const campaignListReducer = CampaignListSlice.reducer